import { Flex, Grid } from '@radix-ui/themes';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Header } from '~/features/project/components/Header';
import { Workspace } from '~/features/project/components/Workspace';

import { toast } from '~/components/Toast/Toast';
import { projectStore } from '~/stores/project';
import { workspaceStore } from '~/stores/workspace';
import { cn } from '~/utils/classNames';

import { useStore } from '@nanostores/react';
import { DelayedRenderingComponent } from '~/components/DelayedRenderingComponent';
import { DocumentTitle } from '~/components/DocumentTitle';
import { SwitchOrganisation } from '~/components/errors/SwitchOrganisation.tsx';
import { LoadingSpinner } from '~/components/LoadingSpinner/LoadingSpinner';
import { useSession } from '~/features/auth/context/Session';
import Styles from '~/features/project/components/css/workspace.module.scss';
import { setBodyStyles } from '~/helpers/setBodyStyles';
import { useGetEnvironmentConnections } from '~/hooks/useGetEnvironmentConnections';
import { useOrganisation } from '~/hooks/useOrganisation';
import { useProject } from '~/hooks/useProject';
import { useProjectSystems } from '~/hooks/useProjectSystems';
import { defaultQueryClient } from '~/lib/tanstackQuery/DefaultQueryClient';
import { Project } from '~/services/versori-api/types';
import { connectorsStore } from '~/stores/connectors';
import { interfaceStore } from '~/stores/interface';
import { workbenchStore } from '~/stores/workbench';
import RoutesStyles from '~/styles/routes.module.scss';
import { reFormatFiles } from '~/utils/formatFiles';
import { z } from 'zod';

export const Route = createFileRoute('/_auth/integrations/$projectId')({
  component: RouteComponent,
  validateSearch: z.object({
    a: z.boolean().optional(),
  }),
  loader: async () => {
    setBodyStyles(true);
  },
  onLeave: () => {
    setBodyStyles(false);
  },
});

function RouteComponent() {
  const { session, checkSession } = useSession();
  const { organisation } = useOrganisation();
  const { projectId } = Route.useParams();
  const workspaceProject = useStore(projectStore.project);
  const navigate = useNavigate();
  const { project, isError, error } = useProject();
  const currentEnvironment = useStore(projectStore.currentEnvironment);

  const setWorkspaceProject = (project: Project) => {
    projectStore.setProject(project);
    if (project.currentFiles?.files?.length > 0) {
      // Research documents and workflows now live in the workbench alongside
      // regular project files (under documents/research/ and
      // documents/workflows/), so hydrate all files into the workbench store.
      workbenchStore.addWorkbenchFiles({
        files: reFormatFiles(project.currentFiles.files),
      });
    }
  };

  const { data: connectionTemplates } = useProjectSystems({ project: project });

  useEffect(() => {
    if (connectionTemplates) {
      connectorsStore.setConnectionTemplates(connectionTemplates.items || []);
    }
  }, [connectionTemplates]);

  useGetEnvironmentConnections({
    projectId: projectId,
    envId: currentEnvironment.id,
  });

  useEffect(() => {
    if (!session) {
      interfaceStore.setShowExpiredSessionModal(true);
      return;
    }
    if (isError) {
      if (error?.code !== '403') {
        toast({
          title: 'Unable to load project',
          type: 'error',
        });

        navigate({ to: '/integrations' });
        return;
      }
    }
    if (project) {
      setWorkspaceProject(project);
      if (session) {
        defaultQueryClient.invalidateQueries({ queryKey: ['getProjects', organisation?.id] });
      }
    }
  }, [project, organisation?.id, session, checkSession, isError, error, navigate]);

  useEffect(() => {
    return () => {
      workspaceStore.reset();
      // Safety net: clear any prompts that were saved for the login flow but never re-applied
      localStorage.removeItem('pendingChatMessage');
      localStorage.removeItem('pendingPlanChatMessage');
      // If mermaid has thrown an error we need to remove the error div it attaches into the DOM
      const mermaidDiagram = document.querySelector('div[id^=darchitectural-diagram]');
      if (mermaidDiagram) {
        (mermaidDiagram as HTMLElement).remove();
      }
    };
  }, []);

  return (
    <>
      {workspaceProject.id && <DocumentTitle title={`${workspaceProject.name} - Versori`} />}
      <Grid
        height="100vh"
        width="100%"
        areas={`
        "header" "interface"
        `}
        columns="1fr"
        rows="5.5rem 1fr"
      >
        {workspaceProject.id && <Header />}
        <div className={cn(Styles.ProjectPage, 'projectPage')}>
          {workspaceProject.id ? (
            <Workspace />
          ) : isError ? (
            <Flex justify="center" align="center" height="100%" width="100%">
              <SwitchOrganisation />
            </Flex>
          ) : (
            <Flex justify="center" align="center" height="100%" width="100%">
              <DelayedRenderingComponent delay={500}>
                <LoadingSpinner width="6rem" height="6rem" />
              </DelayedRenderingComponent>
            </Flex>
          )}
        </div>
      </Grid>
    </>
  );
}
